import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccountHome.css';
import { API_URL } from '../config/data';
import AccountCard from '../components/AccountCard';
import TradingAccountCard from '../components/TradingAccountCard';
import { logout, checkAuth, handleAuthError } from '../utils/auth';

const AccountHome = () => {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [tradingTotalProfit, setTradingTotalProfit] = useState({});
  const [tradingTotalDeposits, setTradingTotalDeposits] = useState({});
  const [tradingTotalIncome, setTradingTotalIncome] = useState({});
  const [tradingTotalPercentage, setTradingTotalPercentage] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Функция для получения банковских счетов пользователя
  const fetchUserAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Ошибка при получении банковских счетов:', error);
      throw error;
    }
  };

  // Функция для получения торговых счетов пользователя
  const fetchTradingAccounts = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/trading-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Ошибка при получении торговых счетов:', error);
      throw error;
    }
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    if (!checkAuth()) {
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Загружаем профиль, банковские и торговые счета параллельно
        const [userData, accountsData, tradingAccountsData] = await Promise.all([
          fetchUserProfile(),
          fetchUserAccounts(),
          fetchTradingAccounts()
        ]);

        setUser(userData);
        setAccounts(accountsData.accounts || []);
        setTotalBalance(accountsData.totalBalance || 0);
        setTradingAccounts((tradingAccountsData.accounts || []).reverse());
        
        // Вычисляем общую сумму по балансу торговых счетов по валютам
        const totalByCurrency = (tradingAccountsData.accounts || []).reduce((acc, account) => {
          const currency = account.currency || 'USD';
          const balance = parseFloat(account.balance) || 0;
          
          if (!acc[currency]) {
            acc[currency] = 0;
          }
          acc[currency] += balance;
          
          return acc;
        }, {});
        
        // Вычисляем сумму депозитов по валютам
        const totalDepositsByCurrency = (tradingAccountsData.accounts || []).reduce((acc, account) => {
          const currency = account.currency || 'USD';
          const deposit_amount = parseFloat(account.deposit_amount) || 0;
          
          if (!acc[currency]) {
            acc[currency] = 0;
          }
          acc[currency] += deposit_amount;
          
          return acc;
        }, {});
        
        // Вычисляем сумму доходов по валютам
        const totalIncomeByCurrency = (tradingAccountsData.accounts || []).reduce((acc, account) => {
          const currency = account.currency || 'USD';
          const profit = parseFloat(account.profit) || 0;
          
          if (!acc[currency]) {
            acc[currency] = 0;
          }
          acc[currency] += profit;
          
          return acc;
        }, {});

        // Вычисляем суммарный процент по валютам (сумма percentage по всем счетам валюты)
        const totalPercentageByCurrency = (tradingAccountsData.accounts || []).reduce((acc, account) => {
          const currency = account.currency || 'USD';
          const percentage = parseFloat(account.percentage) || 0;
          if (!acc[currency]) {
            acc[currency] = 0;
          }
          acc[currency] += percentage;
          return acc;
        }, {});
        
        // Сохраняем объекты с общими суммами по валютам
        setTradingTotalProfit(totalByCurrency);
        setTradingTotalDeposits(totalDepositsByCurrency);
        setTradingTotalIncome(totalIncomeByCurrency);
        setTradingTotalPercentage(totalPercentageByCurrency);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        setError('Ошибка при загрузке данных');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Функция для форматирования суммы
  const formatBalance = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount);
  };

  // Функция для форматирования процента
  const formatPercentage = (value) => {
    const num = Number(value) || 0;
    return `${num.toFixed(2)}%`;
  };

  // Получение суммарного процента по валюте: сумма полей percentage по счетам этой валюты
  const getTotalPercentageByCurrency = (currency) => {
    const sumPercentage = parseFloat(tradingTotalPercentage[currency] || 0);
    return formatPercentage(sumPercentage);
  };

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wrapper">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--active aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="/account/withdraw" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <div className="account__home home">
                <h3 className="account__title account__title--title-25 home__title">Быстрые действия</h3>
                <div className="home__action action-home">
                  <a href="/account/check" data-fls-block="" className="action-home__item action-home__item--title-20">
                    Пополнить баланс
                    <span>Удобно без комиссий</span>
                  </a>
                  <a href="/account/trading-open" data-fls-block="" className="action-home__item action-home__item--title-20">
                    Открыть торговый счет
                    <span>Практически мгновенно</span>
                  </a>
                  <a href="/account/withdraw" data-fls-block="" className="action-home__item action-home__item--title-20">
                    Обмен валюты
                    <br />
                    и перевод
                  </a>
                </div>
                <div data-fls-block="" data-fls-number="" className="home__number number">
                 

                  {/* Сводный баланс по всем торговым счетам (по валютам), вертикально */}
                  <div className="trading-currency-totals gray__block">
                    <div className="number__label" style={{ fontSize: '1.25rem', fontWeight: 700, color: "black" }}>Всего на торговых счетах</div>
                    {Object.keys(tradingTotalProfit).length > 0 ? (
                      <>
                        {tradingTotalProfit.RUB !== undefined && (
                          <div className="trading-currency-totals__row">
                            <span className="trading-currency-totals__amount">
                              {formatBalance(tradingTotalProfit.RUB || 0)} RUB
                            </span>
                          </div>
                        )}
                        {Object.entries(tradingTotalProfit)
                          .filter(([currency]) => currency !== 'RUB')
                          .map(([currency, amount]) => (
                            <div key={currency} className="trading-currency-totals__row">
                              <span className="trading-currency-totals__amount">
                                {formatBalance(amount || 0)} {currency}
                              </span>
                            </div>
                          ))}
                      </>
                    ) : (
                      <div className="trading-currency-totals__row">
                        <span className="trading-currency-totals__amount">0 RUB</span>
                      </div>
                    )}
                  </div>
                  
                  {tradingAccounts.length > 1 ? (
                    <div className="trading-totals gray__block">
                    {/* Секция депозитов */}
                    <div className="trading-totals__section">
                      <div className="trading-totals__title">Депозит</div>
                      <div className="trading-totals__values">
                        {Object.keys(tradingTotalProfit).length > 0 ? (
                          <>
                            {/* RUB всегда первый */}
                            {tradingTotalProfit.RUB !== undefined && (
                              <div className="trading-totals__row">
                                <span className="trading-totals__amount">
                                  {formatBalance(tradingTotalDeposits.RUB || 0)} RUB
                                </span>
                              </div>
                            )}
                            {/* Остальные валюты */}
                            {Object.entries(tradingTotalProfit)
                              .filter(([currency]) => currency !== 'RUB')
                              .map(([currency]) => (
                                <div key={currency} className="trading-totals__row">
                                  <span className="trading-totals__amount">
                                    {formatBalance(tradingTotalDeposits[currency] || 0)} {currency}
                                  </span>
                                </div>
                              ))}
                          </>
                        ) : (
                          <div className="trading-totals__row">
                            <span className="trading-totals__amount">0 RUB</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Секция доходов */}
                    <div className="trading-totals__section">
                      <div className="trading-totals__title">Доход</div>
                      <div className="trading-totals__values">
                        {Object.keys(tradingTotalProfit).length > 0 ? (
                          <>
                            {/* RUB всегда первый */}
                            {tradingTotalProfit.RUB !== undefined && (
                              <div className="trading-totals__row">
                                <span className="trading-totals__amount">
                                  <span className="trading-totals__amount-value">{formatBalance(tradingTotalIncome.RUB || 0)} RUB</span>
                                  <span className="trading-totals__amount-percentage">{getTotalPercentageByCurrency('RUB')}</span>
                                </span>
                              </div>
                            )}
                            {/* Остальные валюты */}
                            {Object.entries(tradingTotalProfit)
                              .filter(([currency]) => currency !== 'RUB')
                              .map(([currency]) => (
                                <div key={currency} className="trading-totals__row">
                                  <span className="trading-totals__amount">
                                    <span className="trading-totals__amount-value">{formatBalance(tradingTotalIncome[currency] || 0)} {currency}</span>
                                    <span className="trading-totals__amount-percentage">{getTotalPercentageByCurrency(currency)}</span>
                                  </span>
                                </div>
                              ))}
                          </>
                        ) : (
                          <div className="trading-totals__row">
                            <span className="trading-totals__amount">0 RUB</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  ) : (
                    <></>
                  )}
                  
                  
                  {tradingAccounts.length > 0 ? (
                    <div className="accounts-list">
                      {tradingAccounts.map((account, index) => (
                        <TradingAccountCard
                          key={account.id}
                          account={account}
                          index={index}
                          totalAccounts={tradingAccounts.length}
                        />
                      ))}
                    </div>
                  ) : (
                    <div data-fls-block="" className="number__count count">
                      <div className="count__main count__main--icon-usd">
                        <div className="count__content">
                          <h5 className="count__title count__title--title-20">Нет торговых счетов</h5>
                          <div className="count__code">
                            <span>Создайте первый торговый счет</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountHome;